/**
 * Bolds the words a user typed into the unified `/app/search` page
 * inside each result row's subject + snippet. Mirrors what Gmail and
 * Slack do — the eye lands on the matching word much faster than
 * skimming a 200-character preview.
 *
 * The query is tokenized the same way `websearch_to_tsquery` thinks
 * about it, except we only care about which strings to bold, not
 * whether they matched a row:
 *
 *   - Quoted "phrases" stay together as a single highlight term so
 *     phrase searches bold the whole phrase, not the individual
 *     words inside it.
 *   - Bare words become individual highlight terms.
 *   - The `OR` connector and any leading-`-` negation tokens are
 *     dropped — they're search operators, not literal text.
 *
 * Highlighting is done in React (not via `dangerouslySetInnerHTML`),
 * so the source string is never parsed as HTML. That makes this
 * inherently safe against XSS through crafted query strings — the
 * only thing that ever touches the DOM is plain text + a `<mark>`
 * element wrapper React itself constructs.
 */
import type { ReactNode } from "react";

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Pull out the literal terms a user wants highlighted from their raw
 * query string. Returns terms in input order with duplicates kept —
 * the regex builder dedupes by length anyway, and preserving order
 * keeps the function easy to reason about in tests.
 */
export function parseHighlightTerms(query: string): string[] {
  const terms: string[] = [];
  // Token forms, in priority order so the regex engine picks the
  // most specific match at each position:
  //   -"phrase"   → negated phrase: dropped (search operator)
  //   "phrase"    → highlight term, kept whole
  //   -bareWord   → negated word: dropped (search operator)
  //   bareWord    → highlight term (unless it's the OR connector)
  // The two `-…` branches use no capture group — when they fire,
  // every named capture is undefined and the loop body skips.
  const re = /-"([^"]*)"|"([^"]*)"|-\S+|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(query)) !== null) {
    const negatedPhrase = m[1];
    const phrase = m[2];
    const word = m[3];
    if (negatedPhrase !== undefined) continue;
    if (phrase !== undefined) {
      const t = phrase.trim();
      if (t.length > 0) terms.push(t);
      continue;
    }
    if (word !== undefined) {
      // `OR` (case-insensitive) is the disjunction connector and
      // never appears in result text as a meaningful match.
      if (word.toUpperCase() === "OR") continue;
      terms.push(word);
    }
  }
  return terms;
}

/**
 * Render `text` as a React fragment with every occurrence of any
 * query term wrapped in a `<mark>`. Case-insensitive. When the query
 * has no highlightable terms (or the text is empty), returns the
 * input string verbatim — no wrapping element so callers can drop
 * the result anywhere a string would normally go.
 */
export function highlightText(text: string, query: string): ReactNode {
  if (!text) return text;
  const terms = parseHighlightTerms(query);
  if (terms.length === 0) return text;

  // Sort longest-first so e.g. a query of `foo foobar` highlights
  // `foobar` as one mark instead of `foo` + `bar` leftover. Regex
  // alternation picks the first alternative that matches at a given
  // position, which is exactly what we want once sorted.
  const pattern = terms
    .map(escapeRegExp)
    .sort((a, b) => b.length - a.length)
    .join("|");
  const re = new RegExp(`(${pattern})`, "gi");
  // String.split with a capture group interleaves matched text at
  // odd indices and surrounding text at even indices.
  const parts = text.split(re);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <mark
        key={i}
        className="rounded-sm bg-yellow-200 px-0.5 text-foreground dark:bg-yellow-500/30"
      >
        {part}
      </mark>
    ) : (
      part
    ),
  );
}
